<script setup lang="ts">
import { onBeforeUnmount, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import type { EntrScannerPort, ScannerLease } from '@/kiosk-core/types';
import EntrChrome from '@/ui/components/EntrChrome.vue';
import { useScanTicketController } from './controller';
import type { ScanTicketScreenEvents } from './types';

const props = withDefaults(
  defineProps<{
    isBusy?: boolean;
    scanner: EntrScannerPort;
  }>(),
  {
    isBusy: false,
  }
);
const emit = defineEmits<ScanTicketScreenEvents>();
const controller = useScanTicketController(emit, () => props.isBusy);
const rawInput = controller.rawInput;
const scanInputRef = controller.scanInputRef;
const { t } = useI18n();
let scannerLease: ScannerLease | null = null;

onMounted(() => {
  scannerLease = props.scanner.acquire('scan-ticket-screen');
});
onBeforeUnmount(() => {
  scannerLease?.release();
  scannerLease = null;
});
</script>

<template>
  <section
    class="screen scan-screen"
    data-testid="screen-scan-ticket"
    @click="controller.focusInput"
  >
    <h1 class="t-headline headline reveal" style="--reveal-order: 0">{{ t('scan.title') }}</h1>
    <p class="body reveal" style="--reveal-order: 1">{{ t('scan.body') }}</p>

    <input
      ref="scanInputRef"
      v-model="rawInput"
      aria-hidden="true"
      autocomplete="off"
      autocapitalize="off"
      autocorrect="off"
      class="scan-input"
      data-testid="scan-ticket-input"
      inputmode="none"
      spellcheck="false"
      type="text"
      @input="controller.handleInput"
      @keydown="controller.handleKeydown"
    />

    <div class="target reveal" style="--reveal-order: 2" data-testid="scan-target">
      <span class="corner tl" />
      <span class="corner tr" />
      <span class="corner bl" />
      <span class="corner br" />
      <span class="scanline" />
    </div>

    <p
      v-if="props.isBusy"
      class="checking reveal"
      style="--reveal-order: 3"
      data-testid="scan-checking"
      aria-live="polite"
    >
      <span class="spinner" aria-hidden="true" />
      <span>{{ t('scan.checking') }}</span>
    </p>

    <p class="help-text reveal" style="--reveal-order: 3">
      {{ t('scan.help') }}
    </p>

    <EntrChrome :step="1" can-go-back @back="controller.back" @help="controller.help" />
  </section>
</template>

<style scoped>
.scan-input {
  position: fixed;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  border: 0;
  padding: 0;
  opacity: 0;
  pointer-events: none;
}

.headline {
  position: absolute;
  top: 320px;
  left: 0;
  right: 0;
  text-align: center;
}

.body {
  position: absolute;
  top: 480px;
  left: 96px;
  right: 96px;
  color: var(--entr-text-soft);
  font-size: 32px;
  line-height: 1.4;
  text-align: center;
}

.target {
  position: absolute;
  top: 720px;
  left: 240px;
  width: 600px;
  height: 600px;
  animation: pulse 1500ms ease-in-out infinite alternate;
}

.corner {
  position: absolute;
  width: 96px;
  height: 96px;
  border: 8px solid var(--entr-logo-green);
}

.tl {
  top: 0;
  left: 0;
  border-right: 0;
  border-bottom: 0;
}

.tr {
  top: 0;
  right: 0;
  border-left: 0;
  border-bottom: 0;
}

.bl {
  bottom: 0;
  left: 0;
  border-top: 0;
  border-right: 0;
}

.br {
  right: 0;
  bottom: 0;
  border-top: 0;
  border-left: 0;
}

.scanline {
  position: absolute;
  left: 24px;
  right: 24px;
  top: 50%;
  height: 4px;
  background: var(--entr-logo-green);
  box-shadow: 0 0 24px var(--entr-logo-green);
  animation: scanline 1800ms ease-in-out infinite;
}

.help-text {
  position: absolute;
  top: 1480px;
  left: 0;
  right: 0;
  color: var(--entr-text-muted);
  font-size: 26px;
  text-align: center;
}

.checking {
  position: absolute;
  top: 1360px;
  left: 0;
  right: 0;
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
  color: var(--entr-text-soft);
  font-size: 28px;
}

.spinner {
  width: 28px;
  height: 28px;
  border: 4px solid var(--entr-text-muted);
  border-top-color: var(--entr-logo-green);
  border-radius: 999px;
  animation: scan-spin 900ms linear infinite;
}

@keyframes scan-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  from {
    opacity: 0.85;
    transform: scale(1);
  }

  to {
    opacity: 1;
    transform: scale(1.02);
  }
}

@keyframes scanline {
  0% {
    top: 8%;
    opacity: 0;
  }

  20%,
  80% {
    opacity: 1;
  }

  100% {
    top: 92%;
    opacity: 0;
  }
}
</style>
